/**
 * Spec №22.1 (D-CC1) → №29 (S2) — the RUN SUBJECT (mockup D: "the long run, and the
 * moment it needs you").
 *
 * A task is WORK, ON A RECORD, INSIDE A PROCESS, BY AN ACTOR. №22.1 answered that
 * with a two-column cockpit: a CONTEXT RAIL (the matter card + the mini vertical
 * spine) beside the THREAD, so the human never leaves the thread to ask "which
 * matter, where are we, what happens if it finishes?".
 *
 * ## What S2 changed, and why it is a deletion rather than a redesign
 *
 * That rail was correct, and by S1 it had become a duplicate. The shell now carries a
 * persistent WORK RAIL whose four tabs answer the same three questions from the same
 * projections — Context renders the record, the client, trust, unbilled WIP and the
 * soonest key date; Plan renders the instance's own segments and steps, which step is
 * live (the engine's `ready`, not a title match) and the "completing this → X becomes
 * ready" line. So `/app/task/:id` was rendering a SECOND work rail inside the stage,
 * beside the real one — two columns of the same facts, 296px of stage spent saying
 * what the rail was already saying 408px to the right.
 *
 * `TaskContextRail` is therefore deleted, not hidden (D-SS3: removals are
 * deliverables), and the thread runs full-width. The one thing that had to be true
 * for that to be honest is that the shell's rail actually binds to THIS run when you
 * are standing on it — which is `rail-binding.ts`: a `task` route anchor IS the
 * focused run. Before S2 the rail only followed a run that had been clicked in the
 * conversation, so the run subject would have shown its anchor's plan rather than its
 * own.
 *
 * ## The stage head is the run's band now
 *
 * The probe's crumb for this mockup is `Vantage / Disclosure review · ● running · 18m`
 * with the run's verbs on the right, and that is exactly what this page declares
 * (`useStageHead` + `<StageActions>`). Which is why `RunThreadSection` is asked for
 * `chrome="bare"`: its own "◎ RUN · THE EXECUTION THREAD" eyebrow and Launch/Play row
 * were drawn for a card inside a page, and under a band that already names the run
 * they are a title under a title. In the RAIL it keeps them — there is no band there.
 *
 * Operational UX lives in `/app`, never the console.
 */

import { useParams, Navigate } from "react-router";
import { useTaskDetail } from "../../hooks/use-task-detail";
import { RunThreadSection, RunThreadControls } from "../../components/task-detail/RunThreadSection";
import { StageActions, useStageHead, type StageState } from "./stage-head";

/** "18m" · "2h 14m" — the probe's elapsed figure, from the run's own start stamp. */
function elapsedSince(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

export function TaskThreadPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const query = useTaskDetail(taskId);
  const task = query.data?.task;

  // The stage head's crumb for a run. `state` is REAL or absent: a run that is not
  // executing gets no live chip rather than an invented one (D-ST5 / №22.4.1 F1 —
  // "live means executing now"). A run waiting on a human says so, because that is a
  // fact about the run and the reason mockup D exists.
  const needsYou = !!task?.needs_you_reason?.trim();
  const running = !!task && task.status !== "done" && task.status !== "dropped";
  const since = elapsedSince(task?.updated_at ?? task?.created_at ?? null);
  const state: StageState | null = needsYou
    ? { tone: "warn", label: "waiting on you" }
    : running
      ? { tone: "running", label: since ? `running · ${since}` : "running" }
      : task
        ? { tone: "neutral", label: task.status }
        : null;
  useStageHead({ title: task?.title ?? null, state });

  if (!taskId) return <Navigate to="/app" replace />;

  if (query.isLoading) {
    return (
      <div className="screen">
        <div className="matterhead"><div className="skeleton sk-line" style={{ width: "40%", height: 22 }} /></div>
      </div>
    );
  }
  if (query.error || !query.data || !task) {
    return (
      <div className="screen">
        <div className="error-state">
          <h3>Couldn’t load this task</h3>
          <p>{(query.error as Error)?.message ?? "It may have been archived, or you may not have access."}</p>
        </div>
      </div>
    );
  }

  const agentId = task.agent_id ?? "";
  const agentName = task.agent_name ?? "";
  const brief = (task.instructions?.trim() || task.body?.trim() || "");

  return (
    <div className="screen">
      {/* The run's verbs, in the stage head — the probe's `Pause` / `⤓ Export run`
          band. One instance: `RunThreadSection` is `bare` below, so there is exactly
          one Play mutation and one launch menu on this surface. */}
      <StageActions>
        <RunThreadControls taskId={taskId} agentId={agentId} agentName={agentName || "this hire"} variant="stagehead" />
      </StageActions>

      {/* Who is carrying it. The title, the status and the anchor are all in the
          stage head now, so this line says only the thing the head cannot: whether
          you are watching a member of staff work, or have taken the run over. */}
      <div className="runwho" data-testid="run-who">
        {agentName || "Unassigned"}
        <span className="muted">{task.taken_over_by_user_id ? " · you took over" : " · you’re watching"}</span>
      </div>

      {brief && (
        <div className="card">
          <div className="card__h"><span className="eyebrow">Brief</span> Instructions <span className="sub">the one briefing a human or the hire reads</span></div>
          <div className="card__b"><p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{brief}</p></div>
        </div>
      )}
      <div className="card">
        <div className="card__b">
          <RunThreadSection taskId={taskId} agentId={agentId} agentName={agentName || "this hire"} task={task} chrome="bare" />
        </div>
      </div>
    </div>
  );
}
